import { useTranslations } from '@/i18n/use-translations';
import { isAgentsEmbed, embedTransport } from '@/lib/agents-embed';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { type UserMode, isValidUserMode } from '@/lib/user-mode';
import { getApiUrl } from '@/lib/api';
import { posthog } from '@/lib/posthog';

/**
 * Auth Context
 *
 * Current: Direct login flow stores JWT in localStorage
 * Future: OAuth session migration will replace localStorage with httpOnly cookies
 *         (fyso_session, fyso_refresh) for enhanced security
 *
 * TODO Phase 4: Migrate to cookie-based sessions
 * - Read token from httpOnly cookie instead of localStorage
 * - Use /api/auth/session for OAuth code exchange
 * - Implement token refresh using fyso_refresh cookie
 */

interface Admin {
  id: string;
  email: string;
  name: string;
  isSuperAdmin?: boolean;
  emailVerified?: boolean;
  plan?: 'free' | 'pro' | 'enterprise' | 'beta';
}

interface Tenant {
  id: string;
  name: string;
  slug: string;
  primaryColor?: string | null;
  appName?: string | null;
  logoUrl?: string | null;
  deploymentType?: 'shared' | 'dedicated';
  dedicatedApiUrl?: string | null;
}

interface Org {
  id: string;
  name: string;
  slug: string;
  plan: string;
  is_personal: boolean;
  my_role: string;
  member_count?: number;
  tenant_count?: number;
  owner_name?: string;
  owner_email?: string;
}

interface AuthContextType {
  admin: Admin | null;
  tenant: Tenant | null;
  token: string | null;
  schemaName: string | null;
  isAuthenticated: boolean;
  isSuperAdmin: boolean;
  isTenantUser: boolean;
  hasTenant: boolean;
  isReady: boolean;
  currentOrg: Org | null;
  orgs: Org[];
  setCurrentOrg: (org: Org) => void;
  setOrgs: (orgs: Org[]) => void;
  userMode: UserMode;
  setUserMode: (mode: UserMode) => void;
  updateAdmin: (updates: Partial<Admin>) => void;
  updateTenant: (updates: Partial<Tenant>) => void;
  logout: () => void;
  getAuthHeader: () => Record<string, string>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const t = useTranslations('agents.embed');
  const [admin, setAdmin] = useState<Admin | null>(null);
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [schemaName, setSchemaName] = useState<string | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [embedError, setEmbedError] = useState(false);
  const [userMode, setUserModeState] = useState<UserMode>('admin');
  // isTenantUser: true when the session belongs to a tenant-created user (not the admin-owner)
  const [isTenantUser, setIsTenantUser] = useState(false);

  const [currentOrg, setCurrentOrgState] = useState<Org | null>(() => {
    if (isAgentsEmbed()) return null;
    try {
      const saved = localStorage.getItem('org');
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  });
  const [orgs, setOrgsState] = useState<Org[]>([]);

  const setCurrentOrg = (org: Org) => {
    setCurrentOrgState(org);
    localStorage.setItem('org', JSON.stringify(org));
  };

  const setOrgs = (orgList: Org[]) => {
    setOrgsState(orgList);
  };

  useEffect(() => {
    if (isAgentsEmbed()) {
      let active = true;
      let generation = 0;
      let initialized = false;
      let interrupted = false;
      const initialize = () => {
        const current = ++generation;
        setEmbedError(false);
        Promise.resolve().then(() => {
          if (!active || current !== generation) return;
          return embedTransport().request('/auth/me');
        }).then((value) => {
          if (!active || current !== generation) return;
          const me = value as { tenant: Tenant; user?: { id: string; email: string; name?: string }; admin?: Admin };
          setTenant(me.tenant);
          setAdmin(me.admin ?? (me.user ? { ...me.user, name: me.user.name ?? me.user.email } : null));
          setIsTenantUser(!!me.user);
          initialized = true;
          setIsReady(true);
        }).catch(() => { if (active && current === generation) setEmbedError(true); });
      };
      const hide = () => {
        if (!initialized) {
          interrupted = true;
          generation++;
        }
      };
      const show = (event: PageTransitionEvent) => {
        if (event.persisted && interrupted) {
          interrupted = false;
          initialize();
        }
      };
      window.addEventListener('pagehide', hide);
      window.addEventListener('pageshow', show);
      initialize();
      return () => {
        active = false;
        window.removeEventListener('pagehide', hide);
        window.removeEventListener('pageshow', show);
      };
    }
    const savedToken = localStorage.getItem('token');
    const savedAdmin = localStorage.getItem('admin');
    const savedTenant = localStorage.getItem('tenant');
    const savedSchema = localStorage.getItem('schemaName');
    const savedMode = localStorage.getItem('userMode');
    const savedIsTenantUser = localStorage.getItem('isTenantUser') === 'true';

    if (savedToken) setToken(savedToken);
    const parsedAdmin = savedAdmin ? JSON.parse(savedAdmin) : null;
    const parsedTenant = savedTenant ? JSON.parse(savedTenant) : null;
    if (parsedAdmin) setAdmin(parsedAdmin);
    if (parsedTenant) setTenant(parsedTenant);
    if (savedSchema) setSchemaName(savedSchema);
    if (isValidUserMode(savedMode)) setUserModeState(savedMode);
    setIsTenantUser(savedIsTenantUser);
    if (parsedAdmin) {
      posthog.identify(parsedAdmin.id, {
        email: parsedAdmin.email,
        tenant: parsedTenant?.slug,
      });
    }
    setIsReady(true);
  }, []);

  // Native branding must not overwrite the Business-owned embed theme.
  useEffect(() => {
    if (isAgentsEmbed()) return;
    if (tenant?.primaryColor) {
      document.documentElement.style.setProperty('--primary', tenant.primaryColor);
    } else {
      document.documentElement.style.removeProperty('--primary');
    }
  }, [tenant?.primaryColor]);

  const updateAdmin = (updates: Partial<Admin>) => {
    if (!admin) return;
    const updated = { ...admin, ...updates };
    setAdmin(updated);
    localStorage.setItem('admin', JSON.stringify(updated));
  };

  const updateTenant = (updates: Partial<Tenant>) => {
    if (!tenant) return;
    const updated = { ...tenant, ...updates };
    setTenant(updated);
    localStorage.setItem('tenant', JSON.stringify(updated));
  };

  const setUserMode = (mode: UserMode) => {
    setUserModeState(mode);
    localStorage.setItem('userMode', mode);
  };

  const logout = async () => {
    if (token) {
      try {
        await fetch(`${getApiUrl()}/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch {
        // ignore
      }
    }

    setToken(null);
    setAdmin(null);
    setTenant(null);
    setSchemaName(null);
    setIsTenantUser(false);
    setCurrentOrgState(null);
    setOrgsState([]);
    localStorage.clear();
    posthog.reset();
    window.location.assign('/login');
  };

  const getAuthHeader = (): Record<string, string> => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  if (isAgentsEmbed() && embedError) return <p role="alert">{t('sesionError')}</p>;
  if (isAgentsEmbed() && !isReady) return <p role="status">{t('conectando')}</p>;

  return (
    <AuthContext.Provider
      value={{
        admin,
        tenant,
        token,
        schemaName,
        isAuthenticated: isAgentsEmbed() ? !!tenant && !!admin : !!token && (!!admin || isTenantUser),
        isSuperAdmin: admin?.isSuperAdmin === true,
        isTenantUser,
        hasTenant: !!tenant,
        isReady,
        currentOrg,
        orgs,
        setCurrentOrg,
        setOrgs,
        userMode,
        setUserMode,
        updateAdmin,
        updateTenant,
        logout,
        getAuthHeader,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
